import { AxiosResponse } from 'axios'
import {
  IResponse,
  ICampaign,
  IRequestGetCampaignsListByProfileId,
  IGetCodeResponse,
  IRequestGetActiveCampaignsIdNameListByProfileId,
  IActiveCampaignIdName,
  WrappedResponse,
  IUpdateQrCampaignRequest,
  IUpdateRedeemCampaignRequest,
  IUpdateLoyaltyCampaignRequest,
  IPublicCampaign,
  IRequestPublicCampaignByCode,
  ICampaignForSelect,
  IRequestGetCampaignsForSelect,
} from 'primsell-shared-layer/types'
import { TransformPayloadReturnT, instanceMainService, transformPayload } from 'primsell-shared-layer/api'
import { replaceUrlParams } from 'primsell-shared-layer/utils'
import { CAMPAIGNS_URLS } from './campaigns.constants'

export class CampaignsAPI {
  static getCampaignDetailsById(id: ICampaign['id']): Promise<AxiosResponse<IResponse<ICampaign>>> {
    return instanceMainService.get(replaceUrlParams(CAMPAIGNS_URLS.CAMPAIGN_BY_ID, { id }))
  }

  static getPublicCampaignDetailsByCode(
    payload: IRequestPublicCampaignByCode
  ): Promise<WrappedResponse<IPublicCampaign>> {
    return instanceMainService.get(replaceUrlParams(CAMPAIGNS_URLS.CAMPAIGN_PUBLIC_BY_CODE, { code: payload.code }))
  }

  static createCampaign(payload: ICampaign): Promise<AxiosResponse<IResponse<ICampaign>>> {
    return instanceMainService.post(CAMPAIGNS_URLS.CAMPAIGN, payload)
  }

  static updateCampaign(payload: ICampaign): Promise<AxiosResponse<IResponse<ICampaign>>> {
    return instanceMainService.patch(replaceUrlParams(CAMPAIGNS_URLS.CAMPAIGN_BY_ID, { id: payload.id }), payload)
  }

  static updateQrCampaign({
    id,
    payload,
  }: {
    id: ICampaign['id']
    payload: IUpdateQrCampaignRequest
  }): Promise<WrappedResponse<ICampaign>> {
    return instanceMainService.patch(
      replaceUrlParams(CAMPAIGNS_URLS.CAMPAIGN_BY_ID_TYPE, { id, type: 'qrCode' }),
      payload
    )
  }

  static updateRedeemCampaign({
    id,
    payload,
  }: {
    id: ICampaign['id']
    payload: IUpdateRedeemCampaignRequest
  }): Promise<WrappedResponse<ICampaign>> {
    return instanceMainService.patch(
      replaceUrlParams(CAMPAIGNS_URLS.CAMPAIGN_BY_ID_TYPE, { id, type: 'code' }),
      payload
    )
  }

  static updateLoyaltyCampaign({
    id,
    payload,
  }: {
    id: ICampaign['id']
    payload: IUpdateLoyaltyCampaignRequest
  }): Promise<WrappedResponse<ICampaign>> {
    return instanceMainService.patch(
      replaceUrlParams(CAMPAIGNS_URLS.CAMPAIGN_BY_ID_TYPE, { id, type: 'loyalty' }),
      payload
    )
  }

  static deleteCampaign(id: ICampaign['id']): Promise<AxiosResponse<IResponse<ICampaign>>> {
    return instanceMainService.delete(replaceUrlParams(CAMPAIGNS_URLS.CAMPAIGN_BY_ID, { id }))
  }

  static toggleCampaignOnSale(payload: {
    campaignId: ICampaign['id']
    onSale: ICampaign['onSale']
  }): Promise<AxiosResponse<IResponse<ICampaign>>> {
    return instanceMainService.patch(
      replaceUrlParams(CAMPAIGNS_URLS.CAMPAIGN_TOGGLE_ON_SALE, { id: payload.campaignId }),
      {
        onSale: payload.onSale,
      }
    )
  }

  static getCampaignsListByProfileId(
    payload: IRequestGetCampaignsListByProfileId
  ): Promise<WrappedResponse<ICampaign[]>> {
    const {
      id,
      pagination: { limit, offset },
    } = payload
    return instanceMainService.get(
      replaceUrlParams(CAMPAIGNS_URLS.CAMPAIGN_LIST_BY_PROFILE_ID, {
        id,
        limit: limit.toString(),
        offset: offset.toString(),
      })
    )
  }

  static getActiveCampaignsIdNameListByProfileId({
    id,
    ...payload
  }: IRequestGetActiveCampaignsIdNameListByProfileId): Promise<WrappedResponse<IActiveCampaignIdName[]>> {
    const transformedPayload: TransformPayloadReturnT = transformPayload(payload)

    return instanceMainService.get(
      replaceUrlParams(CAMPAIGNS_URLS.ACTIVE_CAMPAIGN_ID_NAME_LIST_BY_PROFILE_ID, { id }),
      {
        params: transformedPayload,
      }
    )
  }

  static getCampaignsForSelect(payload: IRequestGetCampaignsForSelect): Promise<WrappedResponse<ICampaignForSelect>> {
    const transformedPayload: TransformPayloadReturnT = transformPayload(payload)

    return instanceMainService.get(CAMPAIGNS_URLS.CAMPAIGNS_FOR_SELECT, { params: transformedPayload })
  }

  static generateCodePassword(id: ICampaign['id']): Promise<AxiosResponse<IResponse<any>>> {
    return instanceMainService.post(replaceUrlParams(CAMPAIGNS_URLS.CAMPAIGN_GENERATE_CODE_PASSWORD, { id }))
  }

  static getCode(): Promise<AxiosResponse<IResponse<IGetCodeResponse>>> {
    return instanceMainService.post(CAMPAIGNS_URLS.CAMPAIGN_GET_CODE)
  }
}
